<!-- 商家经营台 - 自提扫码/输码核销（手机端优先） -->
<template>
  <ContentWrap title="自提核销" :body-style="{ padding: '16px', overflow: 'visible' }">
    <div class="verify-page">
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        title="扫描顾客订单详情中的自提二维码，或手工输入 8 位核销码。仅可核销本店订单。"
      />

      <!-- 扫码区 -->
      <div class="scan-panel mb-16px">
        <div v-if="scanning" class="scan-video-wrap">
          <video ref="videoRef" class="scan-video" playsinline muted autoplay />
          <div class="scan-frame" />
          <el-button class="scan-stop" type="danger" plain @click="stopScan">关闭摄像头</el-button>
        </div>
        <div v-else class="scan-entry" @click="startScan">
          <Icon icon="ep:full-screen" :size="36" />
          <div class="scan-entry-title">扫码核销</div>
          <div class="scan-entry-desc">调用摄像头识别顾客出示的核销二维码</div>
        </div>
      </div>

      <!-- 手输 -->
      <el-form label-position="top" @submit.prevent>
        <el-form-item label="手动输入核销码" required>
          <div class="flex gap-8px w-full">
            <el-input
              v-model="verifyCode"
              clearable
              maxlength="32"
              placeholder="请输入 8 位数字核销码"
              @keyup.enter="preview"
            />
            <el-button type="primary" :loading="previewLoading" @click="preview">查询</el-button>
          </div>
        </el-form-item>
      </el-form>

      <!-- 预览 -->
      <el-card v-if="previewOrder" shadow="never" class="preview-card">
        <template #header>
          <div class="flex items-center justify-between">
            <span>待核销订单</span>
            <el-tag type="warning">待核销</el-tag>
          </div>
        </template>
        <div class="preview-row"><span class="label">订单号</span>{{ previewOrder.no }}</div>
        <div class="preview-row">
          <span class="label">实付</span>{{ fenToYuan(previewOrder.payPrice) }} 元
        </div>
        <div class="preview-row">
          <span class="label">收货人</span>
          {{ previewOrder.receiverName || '-' }}
          <span v-if="previewOrder.receiverMobile" class="ml-8px text-gray-500">
            {{ maskMobile(previewOrder.receiverMobile) }}
          </span>
        </div>
        <div v-if="(previewOrder.items || []).length" class="preview-items mt-12px">
          <div
            v-for="item in previewOrder.items"
            :key="item.id"
            class="mb-8px flex min-w-0 items-center"
          >
            <el-image
              :src="item.picUrl"
              class="mr-8px h-40px w-40px shrink-0 rounded-4px"
              fit="cover"
            />
            <span class="min-w-0 flex-1 text-13px line-clamp-1">{{ item.spuName }}</span>
            <span class="ml-8px shrink-0 text-13px text-gray-400">x{{ item.count }}</span>
          </div>
        </div>
        <el-button
          class="mt-16px w-full"
          type="success"
          :loading="submitting"
          @click="confirmPickUp"
        >
          确认核销
        </el-button>
      </el-card>
    </div>
  </ContentWrap>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref } from 'vue'
import { fenToYuan } from '@/utils'
import { normalizePickUpVerifyCode } from '@/utils/pickUpVerifyCode'
import { DeliveryTypeEnum } from '@/utils/constants'
import * as MerchantOrderApi from '@/api/merchant/order'

defineOptions({ name: 'MerchantOrderVerify' })

const message = useMessage()

const verifyCode = ref('')
const previewOrder = ref<any>(null)
const previewLoading = ref(false)
const submitting = ref(false)

const scanning = ref(false)
const videoRef = ref<HTMLVideoElement | null>(null)
let mediaStream: MediaStream | null = null
let detectTimer: number | null = null
let lastHandledCode = ''

const maskMobile = (mobile: string) => {
  const s = String(mobile)
  if (s.length < 7) return s
  return `${s.slice(0, 3)}****${s.slice(-4)}`
}

const preview = async (raw?: string) => {
  const code = normalizePickUpVerifyCode(raw ?? verifyCode.value)
  if (!code) {
    message.warning('请输入或扫描核销码')
    return
  }
  if (!/^\d{8}$/.test(code)) {
    message.warning('核销码应为 8 位数字')
    return
  }
  verifyCode.value = code
  previewLoading.value = true
  previewOrder.value = null
  try {
    const data = await MerchantOrderApi.getOrderByPickUpVerifyCode(code)
    if (data?.deliveryType !== DeliveryTypeEnum.PICK_UP.type) {
      message.error('该订单不是自提订单')
      return
    }
    if (data?.status !== 10) {
      message.error('订单不是待核销状态')
      return
    }
    previewOrder.value = data
  } finally {
    previewLoading.value = false
  }
}

const confirmPickUp = async () => {
  const code = normalizePickUpVerifyCode(verifyCode.value)
  if (!code || !previewOrder.value) return
  try {
    await message.confirm(`确认核销订单 ${previewOrder.value.no} 吗？核销后订单将完成。`)
  } catch {
    return
  }
  submitting.value = true
  try {
    await MerchantOrderApi.pickUpOrderByVerifyCode(code)
    message.success('核销成功')
    previewOrder.value = null
    verifyCode.value = ''
    lastHandledCode = ''
  } finally {
    submitting.value = false
  }
}

const stopScan = () => {
  if (detectTimer != null) {
    window.clearInterval(detectTimer)
    detectTimer = null
  }
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop())
    mediaStream = null
  }
  scanning.value = false
}

const detectOnce = async () => {
  const video = videoRef.value
  if (!video || video.readyState < 2) return
  const Detector = (window as any).BarcodeDetector
  if (!Detector) return
  try {
    const detector = new Detector({ formats: ['qr_code'] })
    const codes = await detector.detect(video)
    const raw = codes?.[0]?.rawValue
    if (!raw) return
    const code = normalizePickUpVerifyCode(raw)
    if (!code || code === lastHandledCode) return
    lastHandledCode = code
    verifyCode.value = code
    stopScan()
    await preview(code)
  } catch {
    // 单帧失败忽略，继续下一帧
  }
}

const startScan = async () => {
  if (!('BarcodeDetector' in window)) {
    message.warning('当前浏览器不支持摄像头扫码，请手动输入核销码，或使用 Chrome / Edge 手机浏览器')
    return
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    message.warning('无法访问摄像头，请改用手动输入')
    return
  }
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' } }
    })
    scanning.value = true
    await nextTick()
    const video = videoRef.value
    if (!video) {
      stopScan()
      return
    }
    video.srcObject = mediaStream
    await video.play()
    detectTimer = window.setInterval(() => {
      void detectOnce()
    }, 500)
  } catch {
    stopScan()
    message.error('打开摄像头失败，请检查权限后重试，或改用手动输入')
  }
}

onBeforeUnmount(() => {
  stopScan()
})
</script>

<style scoped lang="scss">
.verify-page {
  max-width: 560px;
  margin: 0 auto;
}
.scan-panel {
  border: 1px dashed var(--el-border-color);
  border-radius: 12px;
  overflow: hidden;
  background: var(--el-fill-color-lighter);
}
.scan-entry {
  padding: 36px 16px;
  text-align: center;
  cursor: pointer;
  color: var(--el-text-color-regular);
  &:hover {
    background: var(--el-fill-color);
  }
}
.scan-entry-title {
  margin-top: 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}
.scan-entry-desc {
  margin-top: 6px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.scan-video-wrap {
  position: relative;
  background: #000;
}
.scan-video {
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: cover;
}
.scan-frame {
  pointer-events: none;
  position: absolute;
  inset: 18%;
  border: 2px solid rgba(64, 158, 255, 0.85);
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.35);
}
.scan-stop {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
}
.preview-card {
  .preview-row {
    font-size: 14px;
    line-height: 1.8;
    .label {
      display: inline-block;
      width: 64px;
      color: var(--el-text-color-secondary);
    }
  }
}
</style>
